import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { STEP_UP_TOKEN_TTL_MS } from "../../application/hooks/useStepUpVerification";
import { useChangePassword } from "../../application/hooks/useChangePassword";
import { ChangePasswordError } from "../../infrastructure/api/AuthApi";
import { passwordSchema } from "../../domain/validation/passwordSchema";
import { AUTH_FLOW_PURPOSE_CHANGE_PASSWORD } from "../../domain/constants/authFlowConstants";
import type { OtpPreference } from "../../domain/constants/otpConstants";
import { PasswordField } from "./PasswordField";
import { VerifyIdentityStep } from "./VerifyIdentityStep";

type Step = "currentPassword" | "verify" | "newPassword";

const STEP_NUMBER: Record<Step, number> = {
  currentPassword: 1,
  verify: 2,
  newPassword: 3,
};

// change-password reports a wrong current password as a plain 400; send the
// user back to step 1 for that case and keep the verification token.
const isWrongCurrentPasswordMessage = (message: string) =>
  /(current|old)[\s_-]*password|incorrect password|invalid password/i.test(message);

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string | null;
  totpEnabled: boolean;
  defaultMethod: OtpPreference;
  /** Called after the authenticator is set up inside the dialog so the page can refetch. */
  onAuthenticatorEnabled?: () => void;
}

export const ChangePasswordDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  email,
  totpEnabled,
  defaultMethod,
  onAuthenticatorEnabled,
}) => {
  const { t } = useTranslation("settings");
  const { t: tAuth } = useTranslation("auth");
  const { changePassword, loading } = useChangePassword();

  const [step, setStep] = useState<Step>("currentPassword");
  const [currentPassword, setCurrentPassword] = useState("");
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [tempTokenExpiresAt, setTempTokenExpiresAt] = useState(0);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setStep("currentPassword");
    setCurrentPassword("");
    setTempToken(null);
    setTempTokenExpiresAt(0);
    setVerifyNotice(null);
    setNewPassword("");
    setConfirmPassword("");
    setFieldErrors({});
    setFormError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const goTo = (next: Step) => {
    setFieldErrors({});
    setFormError(null);
    setStep(next);
  };

  const hasValidToken = () => !!tempToken && Date.now() < tempTokenExpiresAt;

  const restartVerification = () => {
    setTempToken(null);
    setVerifyNotice(t("changePassword.verificationExpired"));
    goTo("verify");
  };

  const handleCurrentPasswordNext = () => {
    if (!currentPassword) {
      setFieldErrors({ currentPassword: t("changePassword.currentPasswordRequired") });
      return;
    }
    // Back here after a wrong current password: the verification is still good.
    goTo(hasValidToken() ? "newPassword" : "verify");
  };

  const handleVerified = (token: string) => {
    setTempToken(token);
    setTempTokenExpiresAt(Date.now() + STEP_UP_TOKEN_TTL_MS);
    setVerifyNotice(null);
    goTo("newPassword");
  };

  const handleChangePassword = async () => {
    const parsed = passwordSchema.safeParse({
      password: newPassword,
      confirmPassword,
    });
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      parsed.error.issues.forEach((issue) => {
        const field = String(issue.path[0]);
        errors[field] ??= tAuth(issue.message);
      });
      setFieldErrors(errors);
      return;
    }
    if (newPassword === currentPassword) {
      setFieldErrors({ password: t("changePassword.sameAsCurrent") });
      return;
    }
    if (!tempToken || !hasValidToken()) {
      restartVerification();
      return;
    }

    try {
      await changePassword(currentPassword, newPassword, tempToken);
      toast.success(t("changePassword.successToast"));
      handleOpenChange(false);
    } catch (err) {
      const message = errorMessage(err, t("changePassword.genericError"));
      if (err instanceof ChangePasswordError && err.status === 401) {
        restartVerification();
        return;
      }
      if (isWrongCurrentPasswordMessage(message)) {
        setCurrentPassword("");
        goTo("currentPassword");
        setFieldErrors({ currentPassword: t("changePassword.currentPasswordIncorrect") });
        return;
      }
      setFormError(message);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (step === "currentPassword") handleCurrentPasswordNext();
    else if (step === "newPassword") void handleChangePassword();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-xl sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("changePassword.title")}
          </DialogTitle>
          <DialogDescription>
            {t("stepOf", { step: STEP_NUMBER[step], total: 3 })}
            {" · "}
            {t(`changePassword.stepTitles.${step}`)}
          </DialogDescription>
        </DialogHeader>

        {step === "verify" ? (
          <VerifyIdentityStep
            purpose={AUTH_FLOW_PURPOSE_CHANGE_PASSWORD}
            email={email}
            totpEnabled={totpEnabled}
            defaultMethod={defaultMethod}
            notice={verifyNotice}
            idPrefix="change-password"
            onAuthenticatorEnabled={onAuthenticatorEnabled}
            onVerified={handleVerified}
            onBack={() => goTo("currentPassword")}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {step === "currentPassword" && (
              <>
                <p className="text-sm text-gray-500">
                  {t("changePassword.currentPasswordHint")}
                </p>
                <PasswordField
                  id="change-password-current"
                  label={t("changePassword.currentPasswordLabel")}
                  value={currentPassword}
                  onChange={(value) => {
                    setCurrentPassword(value);
                    setFieldErrors({});
                  }}
                  error={fieldErrors.currentPassword}
                  disabled={loading}
                  autoFocus
                  autoComplete="current-password"
                />
              </>
            )}

            {step === "newPassword" && (
              <>
                <PasswordField
                  id="change-password-new"
                  label={t("changePassword.newPasswordLabel")}
                  value={newPassword}
                  onChange={(value) => {
                    setNewPassword(value);
                    setFieldErrors({});
                  }}
                  error={fieldErrors.password}
                  disabled={loading}
                  autoFocus
                  autoComplete="new-password"
                />
                <PasswordField
                  id="change-password-confirm"
                  label={t("changePassword.confirmPasswordLabel")}
                  value={confirmPassword}
                  onChange={(value) => {
                    setConfirmPassword(value);
                    setFieldErrors({});
                  }}
                  error={fieldErrors.confirmPassword}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </>
            )}

            {formError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{formError}</p>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button className="cursor-pointer"
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={loading}
              >
                {t("changePassword.cancel")}
              </Button>
              <Button className="cursor-pointer" type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {step === "currentPassword"
                  ? t("changePassword.next")
                  : t("changePassword.submit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
